import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
  buildWebPageSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { MessageCircle, Users, Zap, ArrowRight } from "lucide-react";

const TITLE = "Qualificação de leads no WhatsApp com IA e CRM | FabChat";
const DESCRIPTION =
  "Qualificação de leads no WhatsApp com agente de IA e CRM integrado: organize funil, histórico e follow-up comercial no mesmo painel.";

const BREADCRUMB = [
  { name: "Início", path: "/" },
  { name: "Qualificação de leads no WhatsApp", path: "/qualificacao-de-leads-no-whatsapp" },
];

export default function QualificacaoLeadsWhatsApp() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, BREADCRUMB);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, DESCRIPTION);
  const webPageSchema = buildWebPageSchema(baseUrl, { path: "/qualificacao-de-leads-no-whatsapp", title: TITLE, description: DESCRIPTION });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath="/qualificacao-de-leads-no-whatsapp" />
      <StructuredData data={[organizationSchema, softwareSchema, breadcrumbSchema, webPageSchema]} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Qualificação de leads no WhatsApp</h1>
          <p className="text-lg text-muted-foreground mb-12">
            Converse no WhatsApp, qualifique por estágio e acompanhe no <strong className="text-foreground">CRM integrado</strong>: funil, histórico e follow-up no mesmo painel.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Como qualificar leads no WhatsApp com a FabChat</h2>
            <p className="text-muted-foreground mb-4">
              As conversas que chegam pelo <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">WhatsApp</Link> viram leads no <Link to="/integracoes/crm" className="text-primary hover:underline">CRM nativo da plataforma</Link>. Você organiza por estágios (novo lead, qualificado, negociação, fechado), vê histórico de mensagens e anotações e pode acionar follow-up automático configurado no <strong className="text-foreground">agente de IA</strong>. Assim você qualifica pelo contexto da conversa e pelo estágio no funil, sem planilhas soltas.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Recursos que apoiam a qualificação</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Histórico de conversas por lead, visível no chat e no CRM.</span>
              </li>
              <li className="flex gap-3">
                <Users className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Funil configurável: arraste leads entre estágios e acompanhe quem está mais quente.</span>
              </li>
              <li className="flex gap-3">
                <Zap className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Follow-up automático pelo agente de IA, no timing que você configurar.</span>
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Para quem faz sentido</h2>
            <p className="text-muted-foreground mb-4">
              Times de vendas ou atendimento que usam WhatsApp para primeiro contato e precisam centralizar leads, estágios e conversas. A qualificação acontece na conversa (com a IA) e na organização do funil (no CRM), tudo na mesma ferramenta.
            </p>
          </section>

          <p className="text-sm text-muted-foreground mb-6">
            Também oferecemos <Link to="/instagram/agente-ia" className="text-primary hover:underline">agente para Instagram</Link>; as conversas dos dois canais entram no mesmo CRM.
          </p>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/planos">Ver planos <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
